import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, Copy, SignOut, LinkSimple, Wallet, UsersThree, CalendarCheck, TrendUp } from "@phosphor-icons/react";
import distApi from "@/lib/distApi";
import { initI18n, t } from "@/lib/i18n";
import { errDetail } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bg-white border border-gray-200 rounded-lg p-4">
    <p className="text-[11px] font-semibold text-gray-400 flex items-center gap-1.5 uppercase"><Icon size={13} /> {label}</p>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

const earningLabel = (r) => {
  const st = r.earning_status.state;
  if (st === "lifetime") return t("dist.earn_lifetime", "Earning — lifetime");
  if (st === "active") return t("dist.earn_until", "Earning until {date}", { date: r.commission_until });
  return t("dist.earn_ended", "Earned for {n} months ({from} → {to}) — window ended",
    { n: r.window_months, from: r.registered, to: r.commission_until });
};

const StatusBadge = ({ row }) => {
  const cls = { lifetime: "bg-emerald-50 text-emerald-700", active: "bg-emerald-50 text-emerald-700", ended: "bg-gray-100 text-gray-500" }[row.earning_status.state];
  return <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${cls}`}>{row.earning_status.state === "ended" ? "⚪" : "🟢"} {earningLabel(row)}</span>;
};

export default function DistributorPanel() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [brandName, setBrandName] = useState("Agentic Inbox");
  const [, setI18nReady] = useState(false);

  useEffect(() => {
    initI18n().then(() => setI18nReady(true));
    distApi.get("/site/landing").then((r) => setBrandName(r.data?.brand?.app_name || "Agentic Inbox")).catch(() => {});
  }, []);
  const [refs, setRefs] = useState([]);
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    distApi.get("/distributor/me").then((r) => setMe(r.data)).catch(() => {});
    distApi.get("/distributor/wallet").then((r) => setWallet(r.data)).catch(() => {});
    distApi.get("/distributor/referrals").then((r) => setRefs(r.data)).catch(() => {});
    distApi.get("/distributor/ledger").then((r) => setHist(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!localStorage.getItem("agi_dist_token")) { navigate("/distributors"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const link = me ? `${window.location.origin}/d/${me.ref_code}` : "";
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success(t("dist.link_copied", "Link copied")));
  const logout = () => { localStorage.removeItem("agi_dist_token"); navigate("/distributors"); };

  const requestPayout = async () => {
    setBusy(true);
    try {
      const { data } = await distApi.post("/distributor/payout-request");
      toast.success(t("dist.payout_toast", "Payout of ${amount} requested — we'll review it shortly", { amount: data.amount.toFixed(2) }));
      loadAll();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  if (!me) return <div data-testid="dist-panel-loading" className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-400">{t("dist.loading", "Loading…")}</div>;

  return (
    <div data-testid="distributor-panel" className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Handshake size={22} className="text-blue-600" />
          <div>
            <h1 className="font-heading text-lg font-bold text-gray-900">{t("dist.panel_title", "Distributor Panel")}</h1>
            <p className="text-xs text-gray-400">{t("dist.partner_program", "{brand} partner program", { brand: brandName })}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span data-testid="dist-panel-name" className="text-sm text-gray-600">{me.name} · {me.email}</span>
            <span data-testid="dist-panel-status" className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${me.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>{t(`dist.status_${me.status}`, me.status)}</span>
            <button data-testid="dist-logout" onClick={logout} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-300 rounded-md px-2.5 py-1.5"><SignOut size={13} /> {t("dist.logout", "Log out")}</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={TrendUp} label={t("dist.stat_earned", "Lifetime earned")} value={`$${(wallet?.lifetime_earned ?? 0).toFixed(2)}`} testId="dist-stat-earned" />
          <Stat icon={Wallet} label={t("dist.stat_balance", "Wallet balance")} value={`$${(wallet?.balance ?? 0).toFixed(2)}`} testId="dist-stat-balance" />
          <Stat icon={UsersThree} label={t("dist.stat_referrals", "Referred users")} value={`${wallet?.referred_count ?? 0}`} testId="dist-stat-referrals" />
          <Stat icon={CalendarCheck} label={t("dist.stat_clicks", "Link clicks")} value={`${wallet?.clicks ?? 0}`} testId="dist-stat-clicks" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-1.5"><LinkSimple size={14} /> {t("dist.promo_link", "YOUR PROMO LINK")}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code data-testid="dist-ref-link" className="text-sm bg-gray-50 border border-gray-200 rounded-md px-3 py-2 font-mono text-gray-800 flex-1 min-w-[200px] truncate">{link}</code>
              <button data-testid="dist-copy-link" onClick={copy} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2"><Copy size={14} /> {t("dist.copy", "Copy")}</button>
            </div>
            <p data-testid="dist-commission-info" className="text-xs text-gray-500 mt-3">
              {t("dist.your_commission", "Your commission")}: <b className="text-gray-800">{me.commission_pct}%</b> · {t("dist.category", "Category")}: <b className="text-gray-800">{me.category_name}</b> · {t("dist.window_per_user", "Window per user")}:{" "}
              <b className="text-gray-800">{me.commission_window_months ? t("dist.n_months", "{n} months", { n: me.commission_window_months }) : t("dist.lifetime", "lifetime")}</b>
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-1.5"><Wallet size={14} /> {t("dist.payout_hdr", "PAYOUT")}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <button data-testid="dist-payout-btn" onClick={requestPayout} disabled={busy || !wallet?.can_request_payout}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? t("dist.requesting", "Requesting…") : t("dist.request_payout", "Request payout (${amount})", { amount: (wallet?.balance ?? 0).toFixed(2) })}
              </button>
              {wallet?.pending_payout > 0 && (
                <span data-testid="dist-payout-pending" className="text-[11px] font-semibold bg-amber-50 text-amber-700 rounded-full px-2.5 py-1">{t("dist.pending_review", "⏳ ${amount} pending review", { amount: wallet.pending_payout.toFixed(2) })}</span>
              )}
            </div>
            {!wallet?.can_request_payout && wallet?.payout_blocked_reason && (
              <p data-testid="dist-payout-reason" className="text-xs text-amber-700 mt-2">{wallet.payout_blocked_reason}{wallet.next_payout_date ? ` — ${t("dist.next_possible", "next possible: {date}", { date: wallet.next_payout_date })}` : ""}</p>
            )}
            <p className="text-[11px] text-gray-400 mt-2">{t("dist.payout_rules", "Minimum ${min} · once every {days} days. Paid out: ${paid}.", { min: wallet?.min_payout_usd ?? 100, days: wallet?.payout_interval_days ?? 30, paid: (wallet?.paid_out ?? 0).toFixed(2) })}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <p className="text-xs font-bold text-gray-500 px-5 pt-4 pb-2">{t("dist.referred_hdr", "REFERRED USERS")}</p>
          <table className="w-full text-sm" data-testid="dist-referrals-table">
            <thead className="bg-gray-50 text-left text-[11px] text-gray-400 uppercase">
              <tr><th className="px-5 py-2">{t("dist.th_user", "User")}</th><th className="px-3 py-2">{t("dist.th_registered", "Registered")}</th><th className="px-3 py-2">{t("dist.th_plan", "Plan")}</th>
                <th className="px-3 py-2">{t("dist.th_total_paid", "Total paid")}</th><th className="px-3 py-2">{t("dist.th_your_commission", "Your commission")}</th><th className="px-3 py-2">{t("dist.th_earning_status", "Earning status")}</th></tr>
            </thead>
            <tbody>
              {refs.map((r, i) => (
                <tr key={i} data-testid={`dist-ref-row-${i}`} className={`border-t border-gray-100 ${r.earning_status.state === "ended" ? "opacity-60" : ""}`}>
                  <td className="px-5 py-2.5 font-mono text-gray-700">{r.user}</td>
                  <td className="px-3 py-2.5 text-gray-500">{r.registered}</td>
                  <td className="px-3 py-2.5 text-gray-500">{r.plan}</td>
                  <td className="px-3 py-2.5">${r.total_paid.toFixed(2)}</td>
                  <td className="px-3 py-2.5 font-semibold text-emerald-700">${r.my_commission.toFixed(2)}</td>
                  <td className="px-3 py-2.5" data-testid={`dist-earning-status-${i}`}><StatusBadge row={r} /></td>
                </tr>
              ))}
              {refs.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-400" data-testid="dist-refs-empty">{t("dist.refs_empty", "No referred users yet — share your link to start earning")}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <p className="text-xs font-bold text-gray-500 px-5 pt-4 pb-2">{t("dist.history_hdr", "EARNINGS & PAYOUT HISTORY")}</p>
          <table className="w-full text-sm" data-testid="dist-ledger-table">
            <thead className="bg-gray-50 text-left text-[11px] text-gray-400 uppercase">
              <tr><th className="px-5 py-2">{t("dist.th_date", "Date")}</th><th className="px-3 py-2">{t("dist.th_type", "Type")}</th><th className="px-3 py-2">{t("dist.th_detail", "Detail")}</th>
                <th className="px-3 py-2">{t("dist.th_amount", "Amount")}</th><th className="px-3 py-2">{t("dist.th_balance_after", "Balance after")}</th></tr>
            </thead>
            <tbody>
              {(hist?.payout_requests || []).filter((p) => p.status === "requested").map((p) => (
                <tr key={p.id} data-testid={`dist-payout-req-row-${p.id}`} className="border-t border-gray-100 bg-amber-50/40">
                  <td className="px-5 py-2.5 text-gray-500">{p.requested_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2.5"><span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{t("dist.badge_payout_requested", "PAYOUT REQUESTED")}</span></td>
                  <td className="px-3 py-2.5 text-gray-500">{t("dist.awaiting_review", "Awaiting review")}</td>
                  <td className="px-3 py-2.5 font-semibold">−${p.amount.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-gray-400">—</td>
                </tr>
              ))}
              {(hist?.ledger || []).map((r) => (
                <tr key={r.id} data-testid={`dist-ledger-row-${r.id}`} className="border-t border-gray-100">
                  <td className="px-5 py-2.5 text-gray-500">{r.created_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2.5">
                    {r.type === "commission" && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">{t("dist.badge_commission", "COMMISSION")}</span>}
                    {r.type === "window_expired" && <span className="text-[10px] font-bold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{t("dist.badge_window_ended", "WINDOW ENDED")}</span>}
                    {r.type === "payout" && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">{t("dist.badge_payout", "PAYOUT")}</span>}
                    {r.type === "adjustment" && <span className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">{t("dist.badge_adjustment", "ADJUSTMENT")}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500" title={r.type === "commission" ? `$${r.gross_payment} × ${r.commission_pct}% = $${r.gross_commission} − fee ${r.fee_pct}% − tax ${r.tax_pct}%` : undefined}>
                    {r.note || "—"}{r.user ? ` · ${r.user}` : ""}
                    {r.type === "commission" && <span className="text-[10px] text-gray-400 block">{t("dist.fee_breakdown", "${gross} × {pct}% − {fee}% fee − {tax}% tax", { gross: r.gross_payment, pct: r.commission_pct, fee: r.fee_pct, tax: r.tax_pct })}</span>}
                  </td>
                  <td className={`px-3 py-2.5 font-semibold ${r.net_amount >= 0 ? "text-emerald-700" : "text-blue-700"}`}>{r.net_amount >= 0 ? "+" : "−"}${Math.abs(r.net_amount).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-gray-500">${r.balance_after?.toFixed(2)}</td>
                </tr>
              ))}
              {(!hist || (hist.ledger.length === 0 && hist.payout_requests.length === 0)) && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400" data-testid="dist-ledger-empty">{t("dist.no_earnings", "No earnings yet")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
